// Type definitions for audit data processing

import { normalizeIdToInt } from './normalizers/auditTicketNormalizers';

// Input data types
export interface PreviousAuditDetail {
  game: {
    game_id: string | number;
    name: string;
    cost_per_ticket?: number;
    tickets_per_pack?: number;
  };
  pack_id: string | number;
  starting_ticket_number: string | number;
  ending_ticket_number: string | number;
}

export interface CurrentAuditTicket {
  game: string | number;
  pack: string | number;
  range: string;
  name: string;
}

// Output data types
export interface ProcessedAuditTicket {
  game: string | number;
  pack: string | number;
  range: string;
  name: string;
  isMissing: boolean;
  userAccepted: boolean;
  haveError: boolean;
  errorMessage: string;
  rangeMismatch: boolean;
  rangeMismatchAccepted: boolean;
  newValues: Record<string, any>;
  rangeTooltip: string;
}

export interface MissingGamePack extends ProcessedAuditTicket {
  game: number;
  pack: number;
  isMissing: true;
  haveError: true;
  errorMessage: string;
}

// Function signature with proper typing
export const processAuditDataManual = (
  currentAuditData: CurrentAuditTicket[] | undefined, 
  previousAuditData: PreviousAuditDetail[] | undefined
): ProcessedAuditTicket[] => {

    function getPreviousAuditData(gameId: string, packId: string): PreviousAuditDetail | undefined {
        const normalizedGameId = normalizeIdToInt(gameId);
        const normalizedPackId = normalizeIdToInt(packId);

        return previousAuditData?.find((detail: PreviousAuditDetail) => {
            const detailGameId = normalizeIdToInt(detail.game.game_id);
            const detailPackId = normalizeIdToInt(detail.pack_id);
            if (normalizedGameId === null || normalizedPackId === null || detailGameId === null || detailPackId === null) {
                return String(detail.game.game_id) === String(gameId) && String(detail.pack_id) === String(packId);
            }

            return detailGameId === normalizedGameId && detailPackId === normalizedPackId;
        });
    }

    function isNewGameFunction(gameId: string): boolean {
        const normalizedGameId = normalizeIdToInt(gameId);
        return !previousAuditData?.find((detail: PreviousAuditDetail) => {
            const detailGameId = normalizeIdToInt(detail.game.game_id);
            if (normalizedGameId === null || detailGameId === null) {
                return String(detail.game.game_id) === String(gameId);
            }

            return detailGameId === normalizedGameId;
        });
    }

    function isNewPackFunction(gameId: string, packId: string): boolean {
        const normalizedGameId = normalizeIdToInt(gameId);
        const normalizedPackId = normalizeIdToInt(packId);

        const matchingGames = previousAuditData?.filter((detail: PreviousAuditDetail) => {
            const detailGameId = normalizeIdToInt(detail.game.game_id);
            if (normalizedGameId === null || detailGameId === null) {
                return String(detail.game.game_id) === String(gameId);
            }

            return detailGameId === normalizedGameId;
        });
        const matchingPacks = matchingGames?.filter((detail: PreviousAuditDetail) => {
            const detailPackId = normalizeIdToInt(detail.pack_id);
            if (normalizedPackId === null || detailPackId === null) {
                return String(detail.pack_id) === String(packId);
            }

            return detailPackId === normalizedPackId;
        });
        return matchingPacks?.length == 0;
    }

    function addZeroToRange(length: number, value: any) {
        return '0'.repeat(length) + value;
    }

    function createRange(startingTicket: any, endingTicket: any) {
        // Ensure minimum length is 0, not negative
        let startingRangeLength = Math.max(0, 3 - String(startingTicket)?.length);
        let endingRangeLength = Math.max(0, 3 - String(endingTicket)?.length);

        return addZeroToRange(startingRangeLength, startingTicket) + '-' + addZeroToRange(endingRangeLength, endingTicket);
    }

    function getMissingGamePacks(): MissingGamePack[] {
        let missingGamePacks: MissingGamePack[] = [];
        previousAuditData?.filter((detail: PreviousAuditDetail) => {
            const matchingGame = currentAuditData?.filter((ticket: CurrentAuditTicket) => {
                const currentGameId = normalizeIdToInt(ticket.game);
                const previousGameId = normalizeIdToInt(detail.game.game_id);
                if (currentGameId === null || previousGameId === null) {
                    return String(ticket.game) === String(detail.game.game_id);
                }

                return currentGameId === previousGameId;
            })
            if (matchingGame?.length == 0) {

                missingGamePacks.push({
                    game: Number(detail.game.game_id),
                    pack: Number(detail.pack_id),
                    range: createRange(String(detail.starting_ticket_number), String(detail.ending_ticket_number)),
                    name: detail.game.name,
                    isMissing: true,
                    userAccepted: false,
                    haveError: true,
                    errorMessage: `Missing game pack from previous audit: ${detail.game.name}`,
                    rangeMismatch: false,
                    rangeMismatchAccepted: false,
                    newValues: {},
                    rangeTooltip: ''
                });
            }
        })
        return missingGamePacks;
    }

    const newCurrentAuditData = (currentAuditData || []).map((ticket: CurrentAuditTicket): ProcessedAuditTicket => {

        let data: ProcessedAuditTicket = {
            game: ticket.game,
            pack: ticket.pack,
            range: ticket.range,
            name: ticket.name,
            isMissing: false,
            userAccepted: false,
            haveError: false,
            errorMessage: '',
            rangeMismatch: false,
            rangeMismatchAccepted: false,
            newValues: {},
            rangeTooltip: ''
        }

        let isNewGame = null;
        let isNewPack = null
        let previousData = null;

        // Validate range format: starting ticket should not be higher than ending ticket
        let invalidRangeError = '';
        if (ticket.range && typeof ticket.range === 'string') {
            const rangeParts = ticket.range.split('-').map(part => part.trim());
            if (rangeParts.length >= 2) {
                const startingTicket = Number(rangeParts[0]);
                const endingTicket = Number(rangeParts[1]);
                
                // Check if both are valid numbers
                if (!isNaN(startingTicket) && !isNaN(endingTicket)) {
                    // Check if starting ticket is higher than ending ticket
                    if (startingTicket > endingTicket) {
                        invalidRangeError = `Invalid range: Starting ticket (${rangeParts[0]}) cannot be higher than ending ticket (${rangeParts[1]})`;
                        data.haveError = true;
                    }
                }
            }
        }

        isNewGame = isNewGameFunction(String(ticket.game));

        if (!isNewGame) {
            isNewPack = isNewPackFunction(String(ticket.game), String(ticket.pack));
        }

        if (!isNewPack && !isNewGame) {
            previousData = getPreviousAuditData(String(ticket.game), String(ticket.pack));
        }

        if (isNewGame || isNewPack) {
            data.haveError = true;

            // Base error: identify whether it's a new game or a new pack
            if (isNewGame) {
                data.errorMessage = `New game: ${ticket.game}`;
            } else if (isNewPack) {
                data.errorMessage = `New pack: ${ticket.game} - ${ticket.pack}`;
            }
            
            // If there's an invalid range error, prepend it
            if (invalidRangeError) {
                data.errorMessage = `${invalidRangeError}. ${data.errorMessage}`;
            }

            // Shared check for starting ticket number when a game/pack is new
            // Range format: "000-999", e.g., "001-050"
            // Only check this if we don't already have an invalid range error
            if (!invalidRangeError) {
                let startingTicketString: string | null = null;
                if (ticket.range && typeof ticket.range === 'string') {
                    const rangeParts = ticket.range.split('-').map(part => part.trim());
                    if (rangeParts.length > 0 && rangeParts[0]) {
                        startingTicketString = rangeParts[0];
                    }
                }

                // If it's a new game/pack AND the starting ticket is not "000", append extra info
                if (startingTicketString !== null && startingTicketString !== '000') {
                    data.errorMessage += ` (non-zero starting ticket ${startingTicketString})`;
                }
            }
        } else if (previousData) {
            // If there's an invalid range error, show it
            if (invalidRangeError) {
                data.haveError = true;
                data.errorMessage = invalidRangeError;
            }

            // check for range mismatch
            const currentStartingTicket = Number(ticket.range.split('-')[0]);
            const isRangeMismatch = String(previousData.ending_ticket_number) !== String(currentStartingTicket);

            if (isRangeMismatch) {
                data.rangeMismatch = true;
                data.rangeMismatchAccepted = false;
                data.rangeTooltip = `Range mismatch last ticket ended at: ${previousData.ending_ticket_number}`;
                data.newValues = {
                    ...data.newValues,
                    range: createRange(previousData.ending_ticket_number, ticket.range.split('-')[1])
                }
            }
        } else {
            // For tickets that are not new game/pack and don't have previous data
            // Still check for invalid range
            if (invalidRangeError) {
                data.haveError = true;
                data.errorMessage = invalidRangeError;
            }
        }
        return data;
    });


    let missingGamesPacksData = getMissingGamePacks();

    missingGamesPacksData.forEach((item: MissingGamePack) => {
        newCurrentAuditData.push(item);
    });

    return newCurrentAuditData;

}

export const processPreviousAuditDataForCreateAudit = (auditData: ProcessedAuditTicket[]): ProcessedAuditTicket[] => {
    function addZeroToRange(length: number, value: any) {
        return '0'.repeat(length) + value;
    }

    function createRange(startingTicket: any, endingTicket: any) {
        // Ensure minimum length is 0, not negative
        let startingRangeLength = Math.max(0, 3 - String(startingTicket)?.length);
        let endingRangeLength = Math.max(0, 3 - String(endingTicket)?.length);

        return addZeroToRange(startingRangeLength, startingTicket) + '-' + addZeroToRange(endingRangeLength, endingTicket);
    }

    auditData.sort((a: ProcessedAuditTicket, b: ProcessedAuditTicket) => {
        return Number(a.game) - Number(b.game);
    });

    return auditData.map((item: ProcessedAuditTicket) => {
        return {
            ...item,
            range: item.range?.split('-')[1] || '' ? createRange(item.range?.split('-')[1], item.range?.split('-')[1]) : ''
        }
    });
}
